import { loadBundleSpec } from '@framebridge/toolbox/BundleCartHelpers';
import FrameSpec from '@framebridge/toolbox/FrameSpec';
import {
  eligibleUpsellProducts,
  frameAllowsUpsells,
  upsellPriceCents,
  upsellVariant,
} from '@framebridge/toolbox/upsellEligibility';

/**
 * Line-item upsells for cart lines (easel): products tagged cart-upsell in Shopify come from
 * Joinery with their frame dimension limits, and each cart line is matched on its final frame size.
 */
const joineryHost = (SDG?.Data?.joineryHost || '').replace(/\/$/, '');
const TABLETOP_TAG = 'Tabletop Frames';
const productTagsRequests = new Map();
let productsRequest = null;

function useCartUpsellProducts() {
  const fetchProducts = () => {
    if (!productsRequest) {
      productsRequest = fetch(`${joineryHost}/api/v1/shopify_products_by_tag/cart-upsell`)
        .then((res) => res.json())
        .then((data) => (Array.isArray(data) ? data.map((entry) => entry.product) : []))
        .catch((err) => {
          productsRequest = null;
          console.error('cart upsell products error: ', err);
          return [];
        });
    }

    return productsRequest;
  };

  const productTags = (handle) => {
    if (!productTagsRequests.has(handle)) {
      productTagsRequests.set(handle, fetch(`/products/${handle}.js`)
        .then((res) => res.json())
        .then((product) => product?.tags || [])
        .catch(() => []));
    }

    return productTagsRequests.get(handle);
  };

  const parseFinalFrameSizeProperty = (value) => {
    const match = /([\d.]+)"\s*x\s*([\d.]+)"/.exec(value || '');

    return match ? { width: parseFloat(match[1]), height: parseFloat(match[2]) } : null;
  };

  const bundleFinalFrameSize = async (item) => {
    const { _bundle_spec_number: bundleSpecNumber, _bundle_spec_authorization_token: token } = item.properties || {};
    const bundleSpec = await loadBundleSpec(joineryHost, bundleSpecNumber, token);

    if (bundleSpec?.components?.length !== 1) {
      return null;
    }

    const component = bundleSpec.components[0];
    if (component.frameSpec?.finalFrameSize?.width) {
      return component.frameSpec.finalFrameSize;
    }

    const frameSpec = await FrameSpec.build({ frameSpecData: component.frameSpec, joineryHost, usedFullMats: [] });

    return frameSpec.calculateFinalFrameSize();
  };

  const lineFinalFrameSize = async (item, frameSpec) => {
    if (frameSpec?.finalFrameSize?.width) {
      return frameSpec.finalFrameSize;
    }

    const fromProperty = parseFinalFrameSizeProperty(item.properties?.['Final Frame Size']);
    if (fromProperty) {
      return fromProperty;
    }

    if (frameSpec) {
      return frameSpec.calculateFinalFrameSize();
    }

    if (item.properties?._bundle_spec_number) {
      return bundleFinalFrameSize(item);
    }

    return null;
  };

  const lineFrame = async (item, frameSpec) => ({
    productType: item.product_type,
    isTableTop: frameSpec ? frameSpec.isTableTopFrame() : (await productTags(item.handle)).includes(TABLETOP_TAG),
    isSingleFrame: true,
    isDigital: frameSpec ? frameSpec.isDigital() : !(item.variant_options || []).includes('Physical'),
  });

  const toCartUpsellItem = (product) => {
    const image = (product.media || []).find((media) => media.mediaContentType === 'IMAGE');

    return {
      available: product.status === 'ACTIVE',
      id: product.shopifyProductId,
      title: product.title,
      price: upsellPriceCents(product),
      featured_image: image?.image?.url || null,
      variants: [{ id: upsellVariant(product)?.shopifyVariantId }],
    };
  };

  const lineUpsellItems = async (item, frameSpec = null) => {
    if (!frameAllowsUpsells({ productType: item.product_type })) {
      return [];
    }

    const products = await fetchProducts();
    if (!products.length) {
      return [];
    }

    try {
      const frame = await lineFrame(item, frameSpec);
      if (!frameAllowsUpsells(frame)) {
        return [];
      }

      const finalFrameSize = await lineFinalFrameSize(item, frameSpec);

      return eligibleUpsellProducts({ products, finalFrameSize, ...frame }).map(toCartUpsellItem);
    } catch (err) {
      console.error('cart upsell size error: ', err);

      return [];
    }
  };

  return {
    fetchProducts,
    lineUpsellItems,
  };
}

export default useCartUpsellProducts;
